import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

export type Currency = 'EUR' | 'USD' | 'MAD' | 'CAD';

interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  nameFr: string;
  locale: string;
  position: 'before' | 'after';
}

export const CURRENCIES: Record<Currency, CurrencyConfig> = {
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    nameFr: 'Euro',
    locale: 'fr-FR',
    position: 'after',
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    nameFr: 'Dollar américain',
    locale: 'en-US',
    position: 'before',
  },
  MAD: {
    code: 'MAD',
    symbol: 'DH',
    name: 'Moroccan Dirham',
    nameFr: 'Dirham marocain',
    locale: 'fr-MA',
    position: 'after',
  },
  CAD: {
    code: 'CAD',
    symbol: 'CA$',
    name: 'Canadian Dollar',
    nameFr: 'Dollar canadien',
    locale: 'en-CA',
    position: 'after',
  },
};

interface CurrencyContextType {
  currency: Currency;
  currencyConfig: CurrencyConfig;
  setCurrency: (currency: Currency) => void;
  formatAmount: (amount: number, options?: FormatOptions) => string;
  formatAmountInCurrency: (amount: number, currencyCode: string, options?: FormatOptions) => string;
  formatCompact: (amount: number) => string;
  isSecretMode: boolean;
  toggleSecretMode: () => void;
}

interface FormatOptions {
  showSymbol?: boolean;
  showSign?: boolean;
  decimals?: number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const saved = localStorage.getItem('currency');
    if (saved && (saved === 'EUR' || saved === 'USD' || saved === 'MAD' || saved === 'CAD')) {
      return saved as Currency;
    }
    return 'EUR'; // Default to EUR
  });

  const [isSecretMode, setIsSecretMode] = useState(() => {
    const saved = localStorage.getItem('secretMode');
    return saved === 'true';
  });

  const currencyConfig = CURRENCIES[currency];

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('secretMode', String(isSecretMode));
  }, [isSecretMode]);

  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);
  }, []);

  const toggleSecretMode = useCallback(() => {
    setIsSecretMode((prev) => !prev);
  }, []);

  const formatAmount = useCallback(
    (amount: number, options: FormatOptions = {}) => {
      const { showSymbol = true, showSign = false, decimals = 2 } = options;
      const config = CURRENCIES[currency];

      // If secret mode is enabled, return masked value
      if (isSecretMode) {
        const symbol = showSymbol
          ? config.position === 'before'
            ? `${config.symbol}`
            : ` ${config.symbol}`
          : '';
        const prefix = showSign && amount !== 0 ? (amount > 0 ? '+' : '-') : amount < 0 ? '-' : '';
        return config.position === 'before'
          ? `${prefix}${config.symbol}••••••`
          : `${prefix}••••••${symbol}`;
      }

      const absAmount = Math.abs(amount);
      const formattedNumber = absAmount.toLocaleString(config.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

      let result = '';

      if (showSign && amount !== 0) {
        result += amount > 0 ? '+' : '-';
      } else if (amount < 0) {
        result += '-';
      }

      if (showSymbol) {
        if (config.position === 'before') {
          result += `${config.symbol}${formattedNumber}`;
        } else {
          result += `${formattedNumber} ${config.symbol}`;
        }
      } else {
        result += formattedNumber;
      }

      return result;
    },
    [currency, isSecretMode]
  );

  const formatAmountInCurrency = useCallback(
    (amount: number, currencyCode: string, options: FormatOptions = {}) => {
      const { showSymbol = true, showSign = false, decimals = 2 } = options;
      const config = CURRENCIES[currencyCode as Currency] || CURRENCIES[currency];

      if (isSecretMode) {
        const symbol = showSymbol
          ? config.position === 'before'
            ? `${config.symbol}`
            : ` ${config.symbol}`
          : '';
        const prefix = showSign && amount !== 0 ? (amount > 0 ? '+' : '-') : amount < 0 ? '-' : '';
        return config.position === 'before'
          ? `${prefix}${config.symbol}••••••`
          : `${prefix}••••••${symbol}`;
      }

      const absAmount = Math.abs(amount);
      const formattedNumber = absAmount.toLocaleString(config.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

      let result = '';

      if (showSign && amount !== 0) {
        result += amount > 0 ? '+' : '-';
      } else if (amount < 0) {
        result += '-';
      }

      if (showSymbol) {
        if (config.position === 'before') {
          result += `${config.symbol}${formattedNumber}`;
        } else {
          result += `${formattedNumber} ${config.symbol}`;
        }
      } else {
        result += formattedNumber;
      }

      return result;
    },
    [currency, isSecretMode]
  );

  const formatCompact = useCallback(
    (amount: number) => {
      const config = CURRENCIES[currency];

      // If secret mode is enabled, return masked value
      if (isSecretMode) {
        const sign = amount < 0 ? '-' : '';
        return config.position === 'before'
          ? `${sign}${config.symbol}••••`
          : `${sign}•••• ${config.symbol}`;
      }

      const absAmount = Math.abs(amount);

      let formattedNumber: string;
      if (absAmount >= 1000000) {
        formattedNumber = (absAmount / 1000000).toFixed(1) + 'M';
      } else if (absAmount >= 1000) {
        formattedNumber = (absAmount / 1000).toFixed(1) + 'K';
      } else {
        formattedNumber = absAmount.toFixed(0);
      }

      const sign = amount < 0 ? '-' : '';

      if (config.position === 'before') {
        return `${sign}${config.symbol}${formattedNumber}`;
      } else {
        return `${sign}${formattedNumber} ${config.symbol}`;
      }
    },
    [currency, isSecretMode]
  );

  const value = {
    currency,
    currencyConfig,
    setCurrency,
    formatAmount,
    formatAmountInCurrency,
    formatCompact,
    isSecretMode,
    toggleSecretMode,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
